import { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import getEnvironment, { EnvironmentTypes } from '@zola-helpers/client/dist/es/util/environment';

export enum ZolaRecaptchaV3Actions {
  LOGIN = 'Login',
}

const useRecaptchaV3 = (): ((action?: string) => Promise<string>) => {
  const recaptchaKey =
    getEnvironment() === EnvironmentTypes.PRODUCTION
      ? '6LeBo8MfAAAAANhJVyX-l6gqh9C99g97uCrxsxEy'
      : '6Lc-azsgAAAAADCZ1GQ8yJI4w5YkuWgnyO6L9nBC';

  const [recaptchaId, setRecaptchaId] = useState<number>();
  const recapchaContainerRef = useRef(document.createElement('div'));

  useEffect(() => {
    const { current } = recapchaContainerRef;
    if (document.body) document.body.appendChild(current);
    return () => {
      if (document.body) document.body.removeChild(current);
    };
  }, []);

  createPortal(undefined, recapchaContainerRef.current);

  const onRecaptchaLoad = () => {
    // See https://developers.google.com/recaptcha/docs/invisible#js_api
    window.grecaptcha.ready(() => {
      if (recapchaContainerRef.current) {
        const id: number = grecaptcha.render(
          recapchaContainerRef.current,
          {
            size: 'invisible',
            sitekey: recaptchaKey,
          },
          true
        ); // inherit default attributes);
        setRecaptchaId(id);
      }
    });
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js`;
    script.addEventListener('load', onRecaptchaLoad);
    document.body.appendChild(script);
    () => {
      script.removeEventListener('load', onRecaptchaLoad);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateRecaptchaToken = useCallback(
    async function getToken(action = ZolaRecaptchaV3Actions.LOGIN as string): Promise<string> {
      if (!window.grecaptcha || !(typeof recaptchaId === 'number')) {
        throw Error('Attempting to execute recaptcha before it is available.');
      }
      const stringId = recaptchaId.toString();
      const token = await window.grecaptcha.execute(stringId, {
        action,
      });
      return token;
    },
    [recaptchaId]
  );

  return generateRecaptchaToken;
};

export default useRecaptchaV3;
